import { backgroundFetch } from '@/lib/fetch'
import { langCode2TTSLang } from '.'
import { type SpeakOptions } from './types'
import { v4 as uuidv4 } from 'uuid'

type PlaybackRefs = {
    source: AudioBufferSourceNode
    gain: GainNode
    signal?: AbortSignal
    abortListener?: () => void
    endedListener?: () => void
}

type AudioContextCtor = typeof AudioContext

let sharedAudioContext: AudioContext | null = null
let activePlayback: PlaybackRefs | null = null
let audioContextUnlockRegistered = false

function ensureAudioContext(): AudioContext {
    if (typeof window === 'undefined') {
        throw new Error('AudioContext is not available in this environment')
    }

    const AudioContextConstructor: AudioContextCtor | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext

    if (!AudioContextConstructor) {
        throw new Error('AudioContext constructor is not available in this environment')
    }

    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
        sharedAudioContext = new AudioContextConstructor()
    }

    return sharedAudioContext
}

function registerAudioContextUnlock() {
    if (audioContextUnlockRegistered || typeof window === 'undefined') {
        return
    }

    audioContextUnlockRegistered = true

    const unlock = async () => {
        try {
            const context = ensureAudioContext()
            if (context.state === 'suspended') {
                await context.resume().catch(() => undefined)
            }
        } catch (_error) {
            // ignore unlock errors
        } finally {
            window.removeEventListener('touchstart', unlock)
            window.removeEventListener('pointerdown', unlock)
            window.removeEventListener('click', unlock)
        }
    }

    window.addEventListener('touchstart', unlock, { once: true, passive: true })
    window.addEventListener('pointerdown', unlock, { once: true, passive: true })
    window.addEventListener('click', unlock, { once: true })
}

function cleanupPlayback(playback: PlaybackRefs | null, { stopSource = true }: { stopSource?: boolean } = {}) {
    if (!playback) return

    if (playback.abortListener && playback.signal) {
        try {
            playback.signal.removeEventListener('abort', playback.abortListener)
        } catch (_error) {
            // ignore cleanup errors
        }
    }

    if (playback.endedListener) {
        playback.source.removeEventListener('ended', playback.endedListener)
    }

    playback.abortListener = undefined
    playback.endedListener = undefined
    playback.signal = undefined

    if (stopSource) {
        try {
            playback.source.stop()
        } catch (_error) {
            // ignore stop errors
        }
    }

    try {
        playback.source.disconnect()
    } catch (_error) {
        // ignore disconnect errors
    }

    try {
        playback.gain.disconnect()
    } catch (_error) {
        // ignore disconnect errors
    }

    if (activePlayback === playback) {
        activePlayback = null
    }
}

function mkssml(text: string, voice: string, rate: number, volume: number) {
    return (
        "<speak version='1.0' xmlns='http://www.w3.org/2001/10/synthesis' xml:lang='en-US'>" +
        `<voice name='${voice}'><prosody pitch='+0Hz' rate='${rate}' volume='${volume}'>` +
        `${text}</prosody></voice></speak>`
    )
}

function ssmlHeadersPlusData(requestId: string, timestamp: string, ssml: string) {
    return (
        `X-RequestId:${requestId}\r\n` +
        'Content-Type:application/ssml+xml\r\n' +
        `X-Timestamp:${timestamp}Z\r\n` + // This is not a mistake, Microsoft Edge bug.
        `Path:ssml\r\n\r\n` +
        `${ssml}`
    )
}

function getHeadersAndData(data: string) {
    const headers: { [key: string]: string } = {}
    data.slice(0, data.indexOf('\r\n\r\n'))
        .split('\r\n')
        .forEach((line) => {
            const [key, value] = line.split(':', 2)
            headers[key] = value
        })
    return { headers, data: data.slice(data.indexOf('\r\n\r\n') + 4) }
}

const trustedClientToken = '6A5AA1D4EAFF4E9FB37E23D68491D6F4'
// 备用token（如果主要token失效）
const fallbackClientToken = 'D89E4AA9A20F4E8BA923C8C74C7CF2CF'

// const wssURL =
    'wss://speech.platform.bing.com/consumer/speech/synthesize/' +
    'readaloud/edge/v1?TrustedClientToken=' +
    trustedClientToken

// const fallbackWssURL = 
    'wss://speech.platform.bing.com/consumer/speech/synthesize/' +
    'readaloud/edge/v1?TrustedClientToken=' +
    fallbackClientToken

// WebSocket close code meanings
function getCloseCodeMeaning(code: number): string {
    const codes: Record<number, string> = {
        1000: 'Normal Closure',
        1001: 'Going Away',
        1002: 'Protocol Error',
        1003: 'Unsupported Data',
        1004: 'Reserved',
        1005: 'No Status Received',
        1006: 'Abnormal Closure',
        1007: 'Invalid frame payload data',
        1008: 'Policy Violation',
        1009: 'Message too big',
        1010: 'Missing Extension',
        1011: 'Internal Error',
        1012: 'Service Restart',
        1013: 'Try Again Later',
        1014: 'Bad Gateway',
        1015: 'TLS Handshake'
    };
    return codes[code] || `Unknown (${code})`;
}

// https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/e6faf6b7fc1febb45993b940617719e8ed1358b2/src/sdk/SpeechSynthesizer.ts#L216
const languageToDefaultVoice: { [key: string]: string } = {
    ['af-ZA']: 'af-ZA-AdriNeural',
    ['am-ET']: 'am-ET-AmehaNeural',
    ['ar-AE']: 'ar-AE-FatimaNeural',
    ['ar-BH']: 'ar-BH-AliNeural',
    ['ar-DZ']: 'ar-DZ-AminaNeural',
    ['ar-EG']: 'ar-EG-SalmaNeural',
    ['ar-IQ']: 'ar-IQ-BasselNeural',
    ['ar-JO']: 'ar-JO-SanaNeural',
    ['ar-KW']: 'ar-KW-FahedNeural',
    ['ar-LY']: 'ar-LY-ImanNeural',
    ['ar-MA']: 'ar-MA-JamalNeural',
    ['ar-QA']: 'ar-QA-AmalNeural',
    ['ar-SA']: 'ar-SA-HamedNeural',
    ['ar-SY']: 'ar-SY-AmanyNeural',
    ['ar-TN']: 'ar-TN-HediNeural',
    ['ar-YE']: 'ar-YE-MaryamNeural',
    ['bg-BG']: 'bg-BG-BorislavNeural',
    ['bn-BD']: 'bn-BD-NabanitaNeural',
    ['bn-IN']: 'bn-IN-BashkarNeural',
    ['ca-ES']: 'ca-ES-JoanaNeural',
    ['cs-CZ']: 'cs-CZ-AntoninNeural',
    ['cy-GB']: 'cy-GB-AledNeural',
    ['da-DK']: 'da-DK-ChristelNeural',
    ['de-AT']: 'de-AT-IngridNeural',
    ['de-CH']: 'de-CH-JanNeural',
    ['de-DE']: 'de-DE-KatjaNeural',
    ['el-GR']: 'el-GR-AthinaNeural',
    ['en-AU']: 'en-AU-NatashaNeural',
    ['en-CA']: 'en-CA-ClaraNeural',
    ['en-GB']: 'en-GB-LibbyNeural',
    ['en-HK']: 'en-HK-SamNeural',
    ['en-IE']: 'en-IE-ConnorNeural',
    ['en-IN']: 'en-IN-NeerjaNeural',
    ['en-KE']: 'en-KE-AsiliaNeural',
    ['en-NG']: 'en-NG-AbeoNeural',
    ['en-NZ']: 'en-NZ-MitchellNeural',
    ['en-PH']: 'en-PH-JamesNeural',
    ['en-SG']: 'en-SG-LunaNeural',
    ['en-TZ']: 'en-TZ-ElimuNeural',
    ['en-US']: 'en-US-JennyNeural',
    ['en-ZA']: 'en-ZA-LeahNeural',
    ['es-AR']: 'es-AR-ElenaNeural',
    ['es-BO']: 'es-BO-MarceloNeural',
    ['es-CL']: 'es-CL-CatalinaNeural',
    ['es-CO']: 'es-CO-GonzaloNeural',
    ['es-CR']: 'es-CR-JuanNeural',
    ['es-CU']: 'es-CU-BelkysNeural',
    ['es-DO']: 'es-DO-EmilioNeural',
    ['es-EC']: 'es-EC-AndreaNeural',
    ['es-ES']: 'es-ES-AlvaroNeural',
    ['es-GQ']: 'es-GQ-JavierNeural',
    ['es-GT']: 'es-GT-AndresNeural',
    ['es-HN']: 'es-HN-CarlosNeural',
    ['es-MX']: 'es-MX-DaliaNeural',
    ['es-NI']: 'es-NI-FedericoNeural',
    ['es-PA']: 'es-PA-MargaritaNeural',
    ['es-PE']: 'es-PE-AlexNeural',
    ['es-PR']: 'es-PR-KarinaNeural',
    ['es-PY']: 'es-PY-MarioNeural',
    ['es-SV']: 'es-SV-LorenaNeural',
    ['es-US']: 'es-US-AlonsoNeural',
    ['es-UY']: 'es-UY-MateoNeural',
    ['es-VE']: 'es-VE-PaolaNeural',
    ['et-EE']: 'et-EE-AnuNeural',
    ['fa-IR']: 'fa-IR-DilaraNeural',
    ['fi-FI']: 'fi-FI-SelmaNeural',
    ['fil-PH']: 'fil-PH-AngeloNeural',
    ['fr-BE']: 'fr-BE-CharlineNeural',
    ['fr-CA']: 'fr-CA-SylvieNeural',
    ['fr-CH']: 'fr-CH-ArianeNeural',
    ['fr-FR']: 'fr-FR-DeniseNeural',
    ['ga-IE']: 'ga-IE-ColmNeural',
    ['gl-ES']: 'gl-ES-RoiNeural',
    ['gu-IN']: 'gu-IN-DhwaniNeural',
    ['he-IL']: 'he-IL-AvriNeural',
    ['hi-IN']: 'hi-IN-MadhurNeural',
    ['hr-HR']: 'hr-HR-GabrijelaNeural',
    ['hu-HU']: 'hu-HU-NoemiNeural',
    ['id-ID']: 'id-ID-ArdiNeural',
    ['is-IS']: 'is-IS-GudrunNeural',
    ['it-IT']: 'it-IT-IsabellaNeural',
    ['ja-JP']: 'ja-JP-NanamiNeural',
    ['jv-ID']: 'jv-ID-DimasNeural',
    ['kk-KZ']: 'kk-KZ-AigulNeural',
    ['km-KH']: 'km-KH-PisethNeural',
    ['kn-IN']: 'kn-IN-GaganNeural',
    ['ko-KR']: 'ko-KR-SunHiNeural',
    ['lo-LA']: 'lo-LA-ChanthavongNeural',
    ['lt-LT']: 'lt-LT-LeonasNeural',
    ['lv-LV']: 'lv-LV-EveritaNeural',
    ['mk-MK']: 'mk-MK-AleksandarNeural',
    ['ml-IN']: 'ml-IN-MidhunNeural',
    ['mr-IN']: 'mr-IN-AarohiNeural',
    ['ms-MY']: 'ms-MY-OsmanNeural',
    ['mt-MT']: 'mt-MT-GraceNeural',
    ['my-MM']: 'my-MM-NilarNeural',
    ['nb-NO']: 'nb-NO-PernilleNeural',
    ['nl-BE']: 'nl-BE-ArnaudNeural',
    ['nl-NL']: 'nl-NL-ColetteNeural',
    ['pl-PL']: 'pl-PL-AgnieszkaNeural',
    ['ps-AF']: 'ps-AF-GulNawazNeural',
    ['pt-BR']: 'pt-BR-FranciscaNeural',
    ['pt-PT']: 'pt-PT-DuarteNeural',
    ['ro-RO']: 'ro-RO-AlinaNeural',
    ['ru-RU']: 'ru-RU-SvetlanaNeural',
    ['si-LK']: 'si-LK-SameeraNeural',
    ['sk-SK']: 'sk-SK-LukasNeural',
    ['sl-SI']: 'sl-SI-PetraNeural',
    ['so-SO']: 'so-SO-MuuseNeural',
    ['sr-RS']: 'sr-RS-NicholasNeural',
    ['su-ID']: 'su-ID-JajangNeural',
    ['sv-SE']: 'sv-SE-SofieNeural',
    ['sw-KE']: 'sw-KE-RafikiNeural',
    ['sw-TZ']: 'sw-TZ-DaudiNeural',
    ['ta-IN']: 'ta-IN-PallaviNeural',
    ['ta-LK']: 'ta-LK-KumarNeural',
    ['ta-SG']: 'ta-SG-AnbuNeural',
    ['te-IN']: 'te-IN-MohanNeural',
    ['th-TH']: 'th-TH-PremwadeeNeural',
    ['tr-TR']: 'tr-TR-AhmetNeural',
    ['uk-UA']: 'uk-UA-OstapNeural',
    ['ur-IN']: 'ur-IN-GulNeural',
    ['ur-PK']: 'ur-PK-AsadNeural',
    ['uz-UZ']: 'uz-UZ-MadinaNeural',
    ['vi-VN']: 'vi-VN-HoaiMyNeural',
    ['zh-CN']: 'zh-CN-XiaoxiaoNeural',
    ['zh-HK']: 'zh-HK-HiuMaanNeural',
    ['zh-TW']: 'zh-TW-HsiaoChenNeural',
    ['zu-ZA']: 'zu-ZA-ThandoNeural',
}

function dictReplace(s: string, d: Record<string, string>): string {
    for (const [key, value] of Object.entries(d)) {
        s = s.split(key).join(value)
    }
    return s
}

function escape(data: string, entities: Record<string, string> = {}): string {
    data = data.replace(/&/g, '&amp;')
    data = data.replace(/>/g, '&gt;')
    data = data.replace(/</g, '&lt;')
    if (Object.keys(entities).length > 0) {
        data = dictReplace(data, entities)
    }
    return data
}

function removeIncompatibleCharacters(str: string): string {
    const chars: string[] = Array.from(str)

    for (let idx = 0; idx < chars.length; idx++) {
        const char = chars[idx]
        const code = char.charCodeAt(0)
        if ((code >= 0 && code <= 8) || (code >= 11 && code <= 12) || (code >= 14 && code <= 31)) {
            chars[idx] = ' '
        }
    }

    return chars.join('')
}

function* splitTextByByteLength(text: string, byteLength: number): Generator<string, void, void> {
    if (byteLength <= 0) {
        throw new Error('byteLength must be greater than 0')
    }

    while (text.length > byteLength) {
        // Find the last space in the string
        let splitAt = text.lastIndexOf(' ', byteLength)

        // If no space found, splitAt is byteLength
        splitAt = splitAt !== -1 ? splitAt : byteLength

        // Verify all & are terminated with a ;
        while (text.slice(0, splitAt).includes('&')) {
            const ampersandIndex = text.lastIndexOf('&', splitAt)
            if (text.slice(ampersandIndex, splitAt).includes(';')) {
                break
            }

            splitAt = ampersandIndex - 1
            if (splitAt < 0) {
                throw new Error('Maximum byte length is too small or invalid text')
            }
            if (splitAt === 0) {
                break
            }
        }

        // Append the string to the list
        const newText = text.slice(0, splitAt).trim()
        if (newText.length > 0) {
            yield newText
        }
        if (splitAt === 0) {
            splitAt = 1
        }
        text = text.slice(splitAt)
    }

    text = text.trim()
    if (text.length > 0) {
        yield text
    }
}

function calcMaxMesgSize(voice: string, rate: number, volume: number): number {
    const connectId = uuidv4().replace(/-/g, '')
    const date = new Date().toString()
    const websocketMaxSize: number = 2 ** 16
    const overheadPerMessage: number = ssmlHeadersPlusData(connectId, date, mkssml('', voice, rate, volume)).length + 50 // margin of error

    return websocketMaxSize - overheadPerMessage
}

interface EdgeTTSOptions extends SpeakOptions {
    voice?: string
    rate?: number
    volume?: number
}

export async function speak({
    text,
    lang: lang_,
    onFinish,
    voice,
    rate = 1,
    volume = 100,
    signal,
    onStartSpeaking,
}: EdgeTTSOptions) {
    registerAudioContextUnlock()

    const lang = langCode2TTSLang[lang_ ?? 'en'] ?? 'en-US'
    const edgeVoice = voice ?? languageToDefaultVoice[lang] ?? 'en-US-JennyNeural';

    try {
        // console.log('🌐 使用 Cloudflare Workers EdgeTTS');
        
        if (onStartSpeaking) {
            onStartSpeaking();
        }

        // 准备请求数据 - 使用Cloudflare Workers API格式
        const requestData = {
            input: text,
            voice: edgeVoice,
            speed: Math.max(0.25, Math.min(4.0, rate)), // 确保速度在有效范围内
        };

        // console.log('📤 发送Cloudflare Workers TTS请求:', requestData);

        // 调用Cloudflare Workers EdgeTTS API
        const response = await fetch('https://tts-voice-magic.wenyongqd.workers.dev/v1/audio/speech', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'User-Agent': 'Mozilla/5.0 (compatible; EdgeTTS-Client/1.0)',
            },
            body: JSON.stringify(requestData),
            signal, // 支持取消请求
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            throw new Error(`Cloudflare Workers EdgeTTS API error: ${response.status} ${errorText}`);
        }

        // 获取音频数据
        const audioBuffer = await response.arrayBuffer();
        
        if (audioBuffer.byteLength === 0) {
            throw new Error('Empty audio response from Cloudflare Workers');
        }

        // console.log('✅ 收到Cloudflare Workers音频数据:', audioBuffer.byteLength, 'bytes');

        // 创建/复用音频上下文并播放
        const audioContext = ensureAudioContext();

        try {
            if (audioContext.state === 'suspended') {
                try {
                    await audioContext.resume();
                } catch (resumeError) {
                    console.warn('Failed to resume AudioContext before playback', resumeError);
                }
            }

            const decodedBuffer = await audioContext.decodeAudioData(audioBuffer.slice(0));

            if (activePlayback) {
                cleanupPlayback(activePlayback);
            }

            const audioBufferSource = audioContext.createBufferSource();
            audioBufferSource.buffer = decodedBuffer;
            audioBufferSource.playbackRate.value = Math.max(0.25, Math.min(4.0, rate));

            const gainNode = audioContext.createGain();
            const normalizedVolume = Math.max(0, Math.min(1, (volume ?? 100) / 100));
            gainNode.gain.value = normalizedVolume;

            audioBufferSource.connect(gainNode);
            gainNode.connect(audioContext.destination);

            const playback: PlaybackRefs = {
                source: audioBufferSource,
                gain: gainNode,
            };

            const handleEnded = () => {
                cleanupPlayback(playback, { stopSource: false });
                if (onFinish) {
                    onFinish();
                }
            };

            audioBufferSource.addEventListener('ended', handleEnded, { once: true });
            playback.endedListener = handleEnded;

            if (signal) {
                const onAbort = () => {
                    cleanupPlayback(playback);
                };
                signal.addEventListener('abort', onAbort, { once: true });
                playback.signal = signal;
                playback.abortListener = onAbort;
            }

            activePlayback = playback;
            audioBufferSource.start();
            // console.log('🎵 开始播放Cloudflare Workers音频');
            
        } catch (decodeError) {
            console.error('Cloudflare Workers音频解码失败:', decodeError);
            cleanupPlayback(activePlayback);
            throw new Error('Failed to decode audio from Cloudflare Workers');
        }
        
    } catch (error) {
        console.error('Cloudflare Workers EdgeTTS 调用失败:', error);
        throw error; // 重新抛出错误，让上层处理回退
    }
}

// 辅助函数：转换速度为 edge-tts 格式
// Unused function
// function speed_to_rate(speed: number): string {
// //     if (speed < 0.25 || speed > 4.0) {
// //         console.warn('Speed out of range (0.25-4.0), clamping to valid range');
// //         speed = Math.max(0.25, Math.min(4.0, speed));
// //     }
// //
// //     const percentage = (speed - 1) * 100;
// //     return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(0)}%`;
// // }
// 
// // 辅助函数：转换音量为 edge-tts 格式
// // Unused function
// // function volume_to_rate(volume: number): string {
//     if (volume < 0 || volume > 100) {
//         console.warn('Volume out of range (0-100), clamping to valid range');
//         volume = Math.max(0, Math.min(100, volume));
//     }
//     
//     // edge-tts 音量格式：相对于默认音量的百分比变化
//     const percentage = volume - 50; // 50 作为默认值
//     return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(0)}%`;
// }

// // function attemptConnection({
//     text,
//     lang: lang_,
//     onFinish,
//     voice,
//     rate = 1,
//     volume = 100,
//     signal,
//     onStartSpeaking,
// }: EdgeTTSOptions, wsUrl: string, token: string, isFallback: boolean) {
//     const lang = langCode2TTSLang[lang_ ?? 'en'] ?? 'en-US'
//     const connectId = uuidv4().replace(/-/g, '')
//     const date = new Date().toString()
//     const audioContext = new AudioContext()
//     const audioBufferSource = audioContext.createBufferSource()
// 
//     const texts = Array.from(splitTextByByteLength(
//         escape(removeIncompatibleCharacters(text)),
//         calcMaxMesgSize(voice ?? languageToDefaultVoice[lang], rate, volume)
//     ))
// 
//     let stopped = false
//     let startSpeaking = false
// 
//     signal.addEventListener(
//         'abort',
//         () => {
//             try {
//                 stopped = true
//                 audioBufferSource.stop()
//             } catch {
//                 // ignore
//             }
//         },
//         { once: true }
//     )
// 
//     return new Promise<void>((resolve, reject) => {
//         let hasError = false
//         
//         for (const text of texts) {
//             try {
//                 const finalWsUrl = `${wsUrl}&ConnectionId=${connectId}`
//                 
//                 if (isFallback) {
//                     console.log('🔄 使用备用token进行连接尝试');
//                 }
//                 
//                 // 简化的连接信息
//                 console.log(`🔗 尝试连接 EdgeTTS WebSocket${isFallback ? ' (备用token)' : ''}:`, finalWsUrl)
//                 
//                 // 仅在开发环境下进行详细测试
//                 if (process.env.NODE_ENV === 'development' && !isFallback) {
//                     // 测试EdgeTTS服务可用性
//                     const testEdgeTTSService = async () => {
//                         try {
//                             const voiceListResponse = await fetch(
//                                 `https://speech.platform.bing.com/consumer/speech/synthesize/readaloud/voices/list?trustedclienttoken=${token}`,
//                                 { method: 'HEAD', mode: 'no-cors' }
//                             );
//                             console.log('🎯 EdgeTTS服务状态:', voiceListResponse.type === 'opaque' ? '可访问' : '不可访问');
//                         } catch (error) {
//                             console.log('🎯 EdgeTTS服务测试失败，但将继续尝试WebSocket连接');
//                         }
//                     };
//                     testEdgeTTSService().catch(console.error);
//                 }
//                 
//                 // 基本环境检查
//                 if (process.env.NODE_ENV === 'development' && !isFallback) {
//                     console.log('🌐 WebSocket环境:', {
//                         supported: typeof WebSocket !== 'undefined',
//                         arrayBuffer: typeof ArrayBuffer !== 'undefined',
//                         secure: window.isSecureContext
//                     });
//                 }
//                 
//                 // 连接计时
//                 const connectionStartTime = performance.now()
//                 
//                 // 尝试通过URL参数传递必要的信息
//                 // 由于浏览器WebSocket API限制，我们无法直接设置Origin header
//                 // 但我们可以尝试添加额外的查询参数来模拟edge-tts的行为
//                 const enhancedWsUrl = `${finalWsUrl}&Sec-MS-GEC=1&Sec-MS-GEC-Version=1-120.0.0.0&ClientHints=%7B%22Sec-CH-UA%22%3A%22%5C%22Not%20A%20Brand%5C%22%3Bv%3D%5C%2299%5C%22%2C%20%5C%22Google%20Chrome%5C%22%3Bv%3D%5C%22120%5C%22%2C%20%5C%22Chromium%5C%22%3Bv%3D%5C%22120%5C%22%22%7D`
//                 
//                 const ws = new WebSocket(enhancedWsUrl)
//                 ws.binaryType = 'arraybuffer'
//                 
//                 console.log('🔧 尝试使用增强的WebSocket URL配置');
//                 
//                 // 添加连接状态监控
//                 console.log('📡 WebSocket 创建成功，初始状态:', {
//                     readyState: ws.readyState,
//                     readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState],
//                     url: ws.url,
//                     protocol: ws.protocol,
//                     extensions: ws.extensions,
//                     binaryType: ws.binaryType
//                 })
//                 
//                 // 监控readyState变化
//                 let lastReadyState = ws.readyState
//                 const stateMonitor = setInterval(() => {
//                     if (ws.readyState !== lastReadyState) {
//                         console.log('📊 WebSocket 状态变化:', {
//                             from: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][lastReadyState],
//                             to: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState],
//                             timestamp: new Date().toISOString(),
//                             elapsed: performance.now() - connectionStartTime
//                         })
//                         lastReadyState = ws.readyState
//                     }
//                     
//                     // 如果连接已关闭，停止监控
//                     if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
//                         clearInterval(stateMonitor)
//                     }
//                 }, 100)
//                 
//                 // 添加错误处理
//                 ws.addEventListener('error', (error) => {
//                     const errorTime = performance.now() - connectionStartTime
//                     console.error('❌ EdgeTTS WebSocket connection failed:', {
//                         error: error,
//                         errorType: error.type,
//                         timeToError: errorTime,
//                         timestamp: new Date().toISOString(),
//                         readyState: ws.readyState,
//                         readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState],
//                         url: ws.url,
//                         protocol: ws.protocol,
//                         extensions: ws.extensions,
//                         target: error.target,
//                         eventPhase: error.eventPhase,
//                         bubbles: error.bubbles,
//                         cancelable: error.cancelable,
//                         defaultPrevented: error.defaultPrevented,
//                         isTrusted: error.isTrusted
//                     })
//                     
//                     // 网络状态检查
//                     console.error('🌐 错误时网络状态:', {
//                         onLine: navigator.onLine,
//                         connection: (navigator as any).connection ? {
//                             effectiveType: (navigator as any).connection.effectiveType,
//                             downlink: (navigator as any).connection.downlink,
//                             rtt: (navigator as any).connection.rtt,
//                             saveData: (navigator as any).connection.saveData
//                         } : 'N/A'
//                     })
//                     
//                     clearInterval(stateMonitor)
//                     hasError = true
//                     if (!startSpeaking) {
//                         startSpeaking = true
//                         onStartSpeaking?.() // 清除loading状态
//                     }
//                     onFinish?.() // 完成流程
//                     reject(new Error(`EdgeTTS WebSocket connection failed after ${errorTime}ms`))
//                 })
//                 
//                 // 添加关闭事件监控
//                 ws.addEventListener('close', (event) => {
//                     const closeTime = performance.now() - connectionStartTime
//                     console.log('🔌 WebSocket 连接关闭:', {
//                         code: event.code,
//                         reason: event.reason,
//                         wasClean: event.wasClean,
//                         timeToClose: closeTime,
//                         timestamp: new Date().toISOString(),
//                         readyState: ws.readyState,
//                         readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState],
//                         closeCodeMeaning: getCloseCodeMeaning(event.code)
//                     })
//                     
//                     clearInterval(stateMonitor)
//                     if (event.code !== 1000 && !hasError) { // 非正常关闭
//                         hasError = true
//                         if (!startSpeaking) {
//                             startSpeaking = true
//                             onStartSpeaking?.()
//                         }
//                         onFinish?.()
//                         reject(new Error(`WebSocket closed unexpectedly: ${event.code} ${event.reason} after ${closeTime}ms`))
//                     }
//                 })
//                 
//                 // 添加连接成功监控
//                 ws.addEventListener('open', (event) => {
//                     const openTime = performance.now() - connectionStartTime
//                     console.log('✅ WebSocket 连接成功:', {
//                         timeToOpen: openTime,
//                         timestamp: new Date().toISOString(),
//                         readyState: ws.readyState,
//                         readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState],
//                         protocol: ws.protocol,
//                         extensions: ws.extensions,
//                         url: ws.url
//                     })
//                     
//                     // 立即发送语音配置和SSML数据
//                     console.log('📤 发送语音配置和SSML数据')
//                     ws.send(
//                     `X-Timestamp:${date}\r\n` +
//                         'Content-Type:application/json; charset=utf-8\r\n' +
//                         'Path:speech.config\r\n\r\n' +
//                         '{"context":{"synthesis":{"audio":{"metadataoptions":{' +
//                         '"sentenceBoundaryEnabled":false,"wordBoundaryEnabled":true},' +
//                         '"outputFormat":"audio-24khz-48kbitrate-mono-mp3"' +
//                         '}}}}\r\n'
//                     )
//                     ws.send(
//                         ssmlHeadersPlusData(
//                             connectId,
//                             date,
//                             mkssml(text, voice ?? languageToDefaultVoice[lang ?? 'en-US'], rate, volume)
//                         )
//                     )
//                 })
// 
//         signal.addEventListener(
//             'abort',
//             () => {
//                 try {
//                     ws.close()
//                 } catch (e: any) {
//                     console.log(e.message)
//                     // ignore
//                 }
//             },
//             { once: true }
//         )
// 
//         let audioData = new ArrayBuffer(0)
//         let downloadAudio = false
//         let startSpeaking = false
//         ws.addEventListener('message', async (event) => {
//             if (typeof event.data === 'string') {
//                 const { headers } = getHeadersAndData(event.data)
//                 const path = headers['Path']
//                 switch (path) {
//                     case 'turn.start':
//                         downloadAudio = true
//                         break
//                     case 'turn.end': {
//                         downloadAudio = false
//                         if (!audioData.byteLength || stopped) {
//                             // 即使没有音频数据，也要调用 onStartSpeaking 来清除 loading 状态
//                             if (!startSpeaking) {
//                                 startSpeaking = true
//                                 onStartSpeaking?.()
//                             }
//                             onFinish?.()
//                             if (!hasError) {
//                                 resolve()
//                             }
//                             return
//                         }
//                         try {
//                             const buffer = await audioContext.decodeAudioData(audioData)
//                             audioBufferSource.buffer = buffer
//                             audioBufferSource.connect(audioContext.destination)
//                             if (!startSpeaking) {
//                                 startSpeaking = true
//                                 onStartSpeaking?.()
//                             }
//                             audioBufferSource.start()
//                             audioBufferSource.addEventListener('ended', () => {
//                                 onFinish?.()
//                                 audioContext.close()
//                                 if (!hasError) {
//                                     resolve()
//                                 }
//                             })
//                         } catch (error) {
//                             console.error('EdgeTTS audio decoding failed:', error)
//                             // 即使解码失败，也要调用 onStartSpeaking 来清除 loading 状态
//                             if (!startSpeaking) {
//                                 startSpeaking = true
//                                 onStartSpeaking?.()
//                             }
//                             // 调用 onFinish 来完成流程
//                             onFinish?.()
//                             audioContext.close()
//                             reject(error)
//                         }
//                         break
//                     }
//                 }
//             } else if (event.data instanceof ArrayBuffer) {
//                 if (!downloadAudio) {
//                     return
//                 }
//                 // See: https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/d071d11d1e9f34d6f79d0ab6114c90eecb02ba1f/src/common.speech/WebsocketMessageFormatter.ts#L46-L47
//                 const dataview = new DataView(event.data)
//                 const headerLength = dataview.getInt16(0)
//                 if (event.data.byteLength > headerLength + 2) {
//                     const newBody = event.data.slice(2 + headerLength)
//                     const newAudioData = new ArrayBuffer(audioData.byteLength + newBody.byteLength)
//                     const mergedUint8Array = new Uint8Array(newAudioData)
//                     mergedUint8Array.set(new Uint8Array(audioData), 0)
//                     mergedUint8Array.set(new Uint8Array(newBody), audioData.byteLength)
//                     audioData = newAudioData
//                 }
//             }
//         })
//             } catch (error) {
//                 console.error('EdgeTTS WebSocket creation failed:', error)
//                 if (!startSpeaking) {
//                     startSpeaking = true
//                     onStartSpeaking?.() // 清除loading状态
//                 }
//                 onFinish?.() // 完成流程
//                 reject(error)
//                 return // 退出循环
//             }
//         }
//     })
// }
// 
const voiceListURL =
    'https://speech.platform.bing.com/consumer/speech/synthesize/' +
    'readaloud/voices/list?trustedclienttoken=' +
    trustedClientToken

interface EdgeVoice {
    FriendlyName: string
    Gender: string
    Locale: string
    ShortName: string
    Name: string
    SuggestedCodec: string
}
export async function fetchEdgeVoices() {
    const response = await backgroundFetch(voiceListURL, {
        headers: {
            'Authority': 'speech.platform.bing.com',
            'Sec-CH-UA': '" Not;A Brand";v="99", "Microsoft Edge";v="91", "Chromium";v="91"',
            'Sec-CH-UA-Mobile': '?0',
            'User-Agent':
                'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.77 Safari/537.36 Edg/91.0.864.41',
            'Accept': '*/*',
            'Sec-Fetch-Site': 'none',
            'Sec-Fetch-Mode': 'cors',
            'Sec-Fetch-Dest': 'empty',
            'Accept-Encoding': 'gzip, deflate, br',
            'Accept-Language': 'en-US,en;q=0.9',
        },
    })
    const voices: EdgeVoice[] = await response.json()
    return voices.map((voice) => ({
        name: voice.FriendlyName,
        lang: voice.Locale,
        voiceURI: voice.Name,
    })) as SpeechSynthesisVoice[]
}
